import type { VisitFormState } from '@app-types/visit';

/**
 * Mapează formularul clinic (visit_forms / UI) către coloanele SOAP din `visits`
 * cerute la semnare pe backend (chief_complaint, history, physical_exam, diagnosis, treatment_plan).
 */
export function buildVisitSoapPatch(
  form: VisitFormState,
  visitRow: Record<string, unknown>
): Record<string, string> {
  const trim = (v: unknown) => String(v ?? '').trim();

  const historyLines = [
    trim(form.anamnesis?.nutrition) && `Nutriție: ${trim(form.anamnesis?.nutrition)}`,
    trim(form.anamnesis?.deworming) && `Deparazitare: ${trim(form.anamnesis?.deworming)}`,
    trim(form.anamnesis?.currentMeds) && `Medicație: ${trim(form.anamnesis?.currentMeds)}`,
    trim(form.anamnesis?.allergies) && `Alergii: ${trim(form.anamnesis?.allergies)}`,
    trim(form.anamnesis?.environment) && `Mediu: ${trim(form.anamnesis?.environment)}`,
    trim(form.anamnesis?.reproduction) && `Reproducere: ${trim(form.anamnesis?.reproduction)}`,
  ].filter(Boolean) as string[];

  let history = historyLines.join('\n');
  if (history.length < 2) {
    history = trim(visitRow.history) || history;
  }

  let physical_exam =
    trim(form.exam?.notes) ||
    (form.exam?.allNormal ? 'Examen clinic: fără anomalii evidente la evaluarea efectuată.' : '');
  if (physical_exam.length < 2) {
    physical_exam = trim(visitRow.physical_exam) || physical_exam;
  }

  let diagnosis = (form.problems || []).map((p) => trim(p.description)).filter(Boolean).join('; ');
  if (diagnosis.length < 2) {
    diagnosis = trim(visitRow.diagnosis) || diagnosis;
  }

  const planParts = [
    trim(form.plan?.investigations) && `Investigații: ${trim(form.plan?.investigations)}`,
    trim(form.plan?.treatments) && `Tratament: ${trim(form.plan?.treatments)}`,
    trim(form.plan?.homeCare) && `Îngrijire: ${trim(form.plan?.homeCare)}`,
  ].filter(Boolean) as string[];

  let treatment_plan = planParts.join('\n');
  if (treatment_plan.length < 2) {
    treatment_plan = trim(visitRow.treatment_plan) || treatment_plan;
  }

  let chief_complaint = trim(visitRow.chief_complaint);
  if (chief_complaint.length < 2 && form.problems?.[0]) {
    chief_complaint = trim(form.problems[0].description);
  }
  if (chief_complaint.length < 2) {
    chief_complaint = trim(visitRow.chief_complaint) || 'Consultație';
  }

  return {
    chief_complaint,
    history,
    physical_exam,
    diagnosis,
    treatment_plan,
  };
}
